/**
 * London Smoke & Cure — shared transactional email chrome.
 * Aligns with `emailtransporter.js` (order confirmation, delivered, etc.): Arial body, #222 text, logo, gold accents #B99040.
 */

export const EMAIL_LOGO_URL =
    'https://cdn.shopify.com/s/files/1/0614/1611/2309/files/mobile-logo.png?v=1679609726';

/** Body copy — matches order confirmation / shipped templates */
export const EMAIL_BODY_FONT_STACK = 'Arial, Helvetica, sans-serif';
export const EMAIL_TEXT_PRIMARY = '#222';
export const EMAIL_HEADING_COLOR = '#444';
export const EMAIL_TEXT_MUTED = '#777';
export const EMAIL_BORDER_LIGHT = '#eee';
export const EMAIL_ACCENT_GOLD = '#B99040';

/** Inline HTML only — sent emails (transporter, Template Studio defaults, newsletter presets). Not app chrome. */
export const EMAIL_LOGO_HEADER_GRADIENT = 'linear-gradient(135deg, #B99040 0%, #8B6F2E 100%)';
export const EMAIL_PRE_BG = '#fafafa';

/** Monospace block for line lists (cart summary, technical lines) */
export const EMAIL_MONO_FONT_STACK = "Consolas, 'Courier New', Courier, monospace";

/** Wholesale storefront (same default as `contactPromotePipelineService`). Override via env for staging. */
export function getWholesalePublicBase() {
    const raw = process.env.REACT_APP_WHOLESALE_PUBLIC_BASE || 'https://wholesale.londonsmokeandcure.co.uk';
    return String(raw).replace(/\/$/, '');
}

/** Absolute URL to the customer cart page */
export function getWholesaleCartUrl() {
    return `${getWholesalePublicBase()}/cart`;
}

function escapeHtmlAttr(s) {
    return String(s ?? '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

/**
 * Gold primary CTA with resolved href (use in inline-built HTML, no merge).
 */
export function emailCartCtaButtonDirectHtml(cartUrl) {
    const href = escapeHtmlAttr(cartUrl);
    return (
        `<div style="text-align:center;margin:20px 0 8px 0;">` +
        `<a href="${href}" style="display:inline-block;background:${EMAIL_ACCENT_GOLD};color:#ffffff;font-family:${EMAIL_BODY_FONT_STACK};font-size:15px;font-weight:600;padding:12px 28px;border-radius:8px;text-decoration:none;">View your cart</a>` +
        `</div>`
    );
}

/**
 * Same button with `--cartUrl--` merge placeholder (use with sendMailMergeEmail).
 */
export function emailCartCtaButtonMergeHtml() {
    return (
        `<div style="text-align:center;margin:20px 0 8px 0;">` +
        `<a href="--cartUrl--" style="display:inline-block;background:${EMAIL_ACCENT_GOLD};color:#ffffff;font-family:${EMAIL_BODY_FONT_STACK};font-size:15px;font-weight:600;padding:12px 28px;border-radius:8px;text-decoration:none;">View your cart</a>` +
        `</div>`
    );
}

/**
 * Opening block: centered logo (same as order delivered / order confirmation fallbacks).
 */
export function emailLogoHeaderHtml() {
    return (
        `<div style="text-align:center;margin-bottom:24px;">` +
        `<img src="${EMAIL_LOGO_URL}" alt="London Smoke &amp; Cure Logo" style="max-width:220px;margin:20px auto 8px;display:block;" />` +
        `</div>`
    );
}

/**
 * Standard footer line for transactional mail.
 */
export function emailStandardFooterHtml() {
    return `<p style="margin-top:24px;">Thank you for choosing <strong>London Smoke &amp; Cure</strong>!</p>`;
}

/**
 * Wrapper: Arial, max-width, primary text colour + logo + inner + footer.
 */
export function wrapTransactionalEmailHtml(innerHtml) {
    return (
        `<div style="font-family:${EMAIL_BODY_FONT_STACK};color:${EMAIL_TEXT_PRIMARY};max-width:640px;margin:0 auto;">` +
        emailLogoHeaderHtml() +
        innerHtml +
        emailStandardFooterHtml() +
        `</div>`
    );
}

/**
 * Styles for cart line-list pre blocks (matches order table greys).
 */
export function emailCartPreBlockStyleAttr() {
    return [
        `background:${EMAIL_PRE_BG}`,
        `border:1px solid ${EMAIL_BORDER_LIGHT}`,
        'padding:14px 16px',
        'border-radius:8px',
        'white-space:pre-wrap',
        `font-family:${EMAIL_MONO_FONT_STACK}`,
        'font-size:13px',
        'color:#333',
        'line-height:1.45',
    ].join(';');
}
